'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import { Badge, EmptyState, IconButton, LazyLoader } from '@/shared/components/ui';
import { formatDateTimeAt } from '@/shared/formatters';
import { baixarArquivoRemoto, triggerBrowserDownload } from '@/shared/utils';
import { FeatherDownload, FeatherEye, FeatherFileText } from '@subframe/core';
import type { DocumentoRowProps, NegociacaoDocumentosTabProps, SectionLabelProps } from '../../../types/documentosTypes';
import type { NegociacaoDocumentoUiItem } from '../../../types/negociacaoDetailTypes';
import { montarSecoesDocumentos } from '../../../utils/documentosSections';
import { AssinaturaAcordoBanner } from './AssinaturaAcordoBanner';

// Pelo barrel e só sob demanda: um import estático dele poria a visão Assinaturas inteira (lista e
// exportação) no chunk do detalhe (review COR-10).
const AssinaturasDaNegociacao = dynamic(() => import('@/features/negociacaoAssinaturas').then(m => m.AssinaturasDaNegociacao), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ORIGEM_LABEL = {
  ESCRITORIO: 'Enviado pelo escritório',
  CONTRAPARTE: 'Enviado pela contraparte'
} as const;
function DocumentoRow(t0) {
  const $ = _c(28);
  const {
    doc,
    onView,
    onDownload
  } = t0;
  let t1;
  if ($[0] !== doc.icon) {
    t1 = <div className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-solid border-neutral-100 bg-neutral-50 text-neutral-500">{doc.icon}</div>;
    $[0] = doc.icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== doc.name) {
    t2 = <span className="text-body-bold font-body-bold text-default-font">{doc.name}</span>;
    $[2] = doc.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = doc.origem === "ESCRITORIO" ? "brand" : "neutral";
  const t4 = ORIGEM_LABEL[doc.origem];
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <Badge variant={t3}>{t4}</Badge>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div className="flex items-center gap-2">{t2}{t5}</div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = doc.size ? `${doc.size} • ` : "";
  let t8;
  if ($[10] !== doc.createdAt) {
    t8 = formatDateTimeAt(doc.createdAt);
    $[10] = doc.createdAt;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <span className="text-caption text-subtext-color">{t7}Enviado em {t8}</span>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t6 || $[16] !== t9) {
    t10 = <div className="flex flex-col gap-1">{t6}{t9}</div>;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t1 || $[19] !== t10) {
    t11 = <div className="flex items-center gap-4">{t1}{t10}</div>;
    $[18] = t1;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== doc || $[22] !== onDownload || $[23] !== onView) {
    t12 = <div className="flex items-center gap-2">{doc.url ? <>{(doc.type === "IMAGE" || doc.type === "DOCUMENT") && <IconButton variant="neutral-tertiary" icon={<FeatherEye />} size="small" onClick={() => onView(doc.url)} />}<IconButton variant="neutral-tertiary" icon={<FeatherDownload />} size="small" onClick={() => onDownload(doc)} /></> : <span className="text-caption text-neutral-400 italic">Indisponível</span>}</div>;
    $[21] = doc;
    $[22] = onDownload;
    $[23] = onView;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <div className="flex items-center justify-between px-6 py-3 transition-colors hover:bg-neutral-50">{t11}{t12}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}
function SectionLabel(t0) {
  const $ = _c(5);
  const {
    label,
    withDivider
  } = t0;
  const t1 = `px-6 pt-5 pb-2 ${withDivider ? "border-t border-solid border-neutral-100" : ""}`;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="text-caption-bold font-caption-bold text-subtext-color tracking-wider uppercase">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function AcordoEmptyState() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-xl text-neutral-500"><FeatherFileText /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-neutral-200 bg-neutral-50 px-6 py-8">{t0}<div className="flex flex-col items-center gap-0.5"><span className="text-body-bold font-body-bold text-default-font">Nenhum contrato gerado</span><span className="text-body font-body text-subtext-color text-center">Nenhum contrato foi gerado para esta negociação.</span></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function NegociacaoDocumentosTab(t0) {
  const $ = _c(35);
  const {
    casoId,
    documentos,
    acordoIds,
    acordos,
    assinaturaPainel
  } = t0;
  const handleDownload = _temp;
  const handleView = _temp2;
  const acordoAtivo = assinaturaPainel?.acordo ?? null;
  let t1;
  if ($[0] !== assinaturaPainel?.signatarios) {
    t1 = assinaturaPainel?.signatarios ?? [];
    $[0] = assinaturaPainel?.signatarios;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const signatarios = t1;
  const temAssinaturaAtiva = Boolean(acordoAtivo) && signatarios.length > 0;
  let t2;
  if ($[2] !== acordoAtivo?.id || $[3] !== acordos) {
    let t3;
    if ($[5] !== acordoAtivo?.id) {
      t3 = acordo => acordo.id === acordoAtivo?.id && acordo.status === "CANCELLED";
      $[5] = acordoAtivo?.id;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = acordos.some(t3);
    $[2] = acordoAtivo?.id;
    $[3] = acordos;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const acordoCancelado = t2;
  let t3;
  if ($[7] !== signatarios) {
    t3 = signatarios.some(_temp3);
    $[7] = signatarios;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const requerConferencia = t3;
  const mostrarTimeline = temAssinaturaAtiva && !acordoCancelado && !requerConferencia;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== acordoAtivo || $[10] !== acordoIds || $[11] !== assinaturaPainel?.etapaContratoConfigurada || $[12] !== casoId || $[13] !== documentos || $[14] !== mostrarTimeline || $[15] !== signatarios || $[16] !== temAssinaturaAtiva) {
    const view = montarSecoesDocumentos({
      documentos,
      acordoIds,
      etapaContratoConfigurada: assinaturaPainel?.etapaContratoConfigurada ?? false,
      temAssinaturaAtiva
    });
    const temAcordoNaLista = view.acordoItems.length > 0;
    t9 = "flex w-full flex-col overflow-y-auto";
    t4 = "overflow-hidden rounded-lg border border-solid border-neutral-200 bg-white shadow-sm";
    let t10;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <h3 className="text-heading-3 font-heading-3 text-default-font">Documentos e Anexos</h3>;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    if ($[24] !== documentos.length) {
      t5 = <div className="flex items-center justify-between border-b border-solid border-neutral-100 px-6 py-4">{t10}<Badge variant="neutral">{documentos.length} arquivos</Badge></div>;
      $[24] = documentos.length;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    t6 = view.modo === "EMPTY" && <EmptyState icon={<FeatherFileText className="text-neutral-500" />} title="Nenhum documento anexado" description={"Nenhum documento ou m\xEDdia foi enviado nesta negocia\xE7\xE3o."} className="py-12" />;
    t7 = view.modo === "FLAT" && <div className="grid grid-cols-1 divide-y divide-neutral-100">{view.outrosItems.map(doc_0 => <DocumentoRow key={doc_0.id} doc={doc_0} onView={handleView} onDownload={handleDownload} />)}</div>;
    t8 = view.modo === "SECTIONS" && <><SectionLabel label="Acordo" />{view.acordoItems.map(doc_1 => <DocumentoRow key={doc_1.id} doc={doc_1} onView={handleView} onDownload={handleDownload} />)}<div className="px-6 pt-1 pb-4">{acordoAtivo && mostrarTimeline ? <><AssinaturaAcordoBanner acordo={acordoAtivo} signatarios={signatarios} /><details className="mt-3 rounded-[8px] border border-neutral-200 bg-white"><summary className="text-brand-600 cursor-pointer px-4 py-3 text-sm font-medium">Ver dados completos das assinaturas</summary><div className="border-t border-neutral-100 p-4"><AssinaturasDaNegociacao casoId={casoId} /></div></details></> : <AssinaturasDaNegociacao casoId={casoId} estadoSemAcordo={temAcordoNaLista ? null : <AcordoEmptyState />} />}</div>{view.mostrarOutros && <><SectionLabel label="Outros arquivos" withDivider={true} /><div className="grid grid-cols-1 divide-y divide-neutral-100">{view.outrosItems.map(doc_2 => <DocumentoRow key={doc_2.id} doc={doc_2} onView={handleView} onDownload={handleDownload} />)}</div></>}</>;
    $[9] = acordoAtivo;
    $[10] = acordoIds;
    $[11] = assinaturaPainel?.etapaContratoConfigurada;
    $[12] = casoId;
    $[13] = documentos;
    $[14] = mostrarTimeline;
    $[15] = signatarios;
    $[16] = temAssinaturaAtiva;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t10;
  if ($[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t10 = <div className={t4}>{t5}{t6}{t7}{t8}</div>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[32] = t10;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}
function _temp3(signatario) {
  return signatario.aConferir || signatario.situacao === "A_CONFERIR" || signatario.motivosConferencia?.length > 0;
}
function _temp2(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}
async function _temp(doc) {
  if (doc.downloadUrl) {
    triggerBrowserDownload(doc.downloadUrl);
    return;
  }
  await baixarArquivoRemoto(doc.url, doc.name);
}